// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Base API Configuration
 * Provides a centralized fetch wrapper with error handling and common configurations.
 *
 * Token acquisition is async (MSAL's acquireTokenSilent) — the wrapper
 * awaits it before every request so the Authorization header always
 * carries a fresh access token.
 */

import { getApiUrl, ENDPOINTS } from '../utils/constants';
import { getTokenAsync } from '../config/authConfig';
import { removeUserData } from './tokenStorage';
import { useShellStore } from '../stores/shellStore';

/**
 * Base fetch wrapper with error handling
 * @param endpoint - API endpoint (relative path)
 * @param options - Fetch options (method, headers, body, etc.)
 * @returns Parsed JSON response
 */
export const apiRequest = async <T = unknown>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const url = getApiUrl(endpoint);

  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Acquire token from MSAL (silent refresh if needed)
  let tokenAttached = false;
  try {
    const token = await getTokenAsync();
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
      tokenAttached = true;
    }
  } catch {
    // Couldn't acquire a token (e.g. silent refresh not ready right after
    // login, or no active account). Do NOT send a header-less request — it
    // would 401 and falsely raise the session-expired modal during initial
    // hydration. getTokenAsync already redirects to CIAM on
    // InteractionRequiredAuthError, so the genuine "needs login" case
    // self-heals; here we just surface an auth error to the caller.
    throw new Error('Not authenticated');
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...(options.headers as Record<string, string>),
    },
  };

  try {
    const response = await fetch(url, config);

    // Handle 401 Unauthorized — token expired or invalid.
    // Clear stale user data, but only raise the session-expired modal when we
    // actually attached a token that the server rejected (a genuine expiry).
    // A 401 with no token attached is bootstrap/not-yet-authenticated — letting
    // the modal fire there bounced freshly-logged-in users mid-hydration.
    //
    // /api/v1/me is special-cased: the BFF currently also returns 401 there
    // when it merely couldn't resolve a profile from IAM (e.g. a mock-data
    // miss), which isn't a real session expiry. Forcing a full MSAL logout
    // on every /me 401 was kicking users out for what's often a transient
    // upstream issue, so surface it as a non-blocking, retryable warning
    // instead and keep any previously-hydrated user data intact.
    if (response.status === 401) {
      const errorData = await response.json().catch(() => ({}));
      if (endpoint === ENDPOINTS.ME) {
        console.warn('[api] /me returned 401 — showing non-blocking profile warning', errorData);
        useShellStore.getState().setProfileLoadError(true);
        throw new Error(errorData.detail || 'Unable to load your profile.');
      }
      removeUserData();
      if (tokenAttached) {
        useShellStore.getState().setSessionExpired(true);
      }
      throw new Error(errorData.detail || 'Session expired. Please log in again.');
    }

    if (endpoint === ENDPOINTS.ME && response.ok) {
      useShellStore.getState().setProfileLoadError(false);
    }

    // Handle non-2xx responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP Error ${response.status}`);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null as T;
    }

    return (await response.json()) as T;
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
};

/**
 * GET request helper
 */
export const get = <T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'GET',
  });
};

/**
 * POST request helper
 */
export const post = <T = unknown>(
  endpoint: string,
  data?: unknown,
  options: RequestInit = {}
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * PUT request helper
 */
export const put = <T = unknown>(
  endpoint: string,
  data?: unknown,
  options: RequestInit = {}
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * PATCH request helper
 */
export const patch = <T = unknown>(
  endpoint: string,
  data?: unknown,
  options: RequestInit = {}
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

/**
 * DELETE request helper
 */
export const del = <T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'DELETE',
  });
};
